// Client-side Google Analytics 4 tag (gtag) for Google Ads conversion attribution. The server-side
// Segment -> GA4 rail counts conversions but can't carry the `gclid`, so Google Ads can't tie them to a
// click. Loading gtag in the browser captures the gclid into the GA4 session, so the `generated_lead`
// event fired here on project-create attributes to the ad click. Meta + LinkedIn keep their server-side
// Segment CAPI path (matched on email); only GA4's lead is client-side.
import { isSessionMonitoringOrigin } from './sessionMonitoring'

// Public GA4 measurement id for qa.replay.io, the same property as the replay.io marketing site so GA4
// cross-domain linking carries the gclid across the replay.io -> qa.replay.io hop. Not a secret.
const MEASUREMENT_ID = 'G-244NMJ9B93'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

// Preview/branch/local builds inherit the prod build-time config, and gtag's _ga cookie only connects
// the replay.io hop on the canonical host, so capture only on the production origin (same rule as
// FullStory/PostHog next door).
function enabled(): boolean {
  return isSessionMonitoringOrigin(window.location.origin)
}

let started = false

/** Load GA4 gtag and start the session. No-ops off the production origin and after the first call. */
export function initGtag(): void {
  if (started || !enabled()) return
  started = true
  try {
    window.dataLayer = window.dataLayer ?? []
    // gtag is a thin shim that queues each call onto dataLayer; gtag.js replays the queue once loaded.
    window.gtag = (...args: unknown[]) => {
      window.dataLayer!.push(args)
    }
    window.gtag('js', new Date())
    window.gtag('config', MEASUREMENT_ID)
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
    document.head.appendChild(script)
  } catch {
    // analytics must never throw into the app
  }
}

/** Fire a GA4 event. No-op off the production origin or before init. */
export function trackGtagEvent(event: string, params?: Record<string, unknown>): void {
  if (!enabled()) return
  try {
    window.gtag?.('event', event, params ?? {})
  } catch {
    // analytics must never throw into the app
  }
}
